// What the workout page does with changes made in the exercise editor (#6428): the client's note,
// the report for a change a session overwrote, and the decisions about a volume or plateau card
// that a coach may already have answered. Pure, so the rules are unit-tested without the page.
// See design-docs/coach-exercise-editing.md.

import { DateTime } from 'luxon';
import { coachChangeStampOf, Plan_ExercisePath, Plan_SubSlot } from '@cf-shared/data-model/fitness-plan';
import { ExerciseChangeDoc, ExerciseChangeField } from '@cf-shared/data-model/exercise-change';
import { Log_Workout } from '@cf-shared/data-model/log-workout';
import { LogRecordState } from '@cf-shared/data-model/user-log';

/** how many change documents the page reads per exercise: enough for the newest coach changes */
export const CHANGES_READ_PER_EXERCISE = 5;

/** how many of the exercise's newest workouts the note searches for the previous one. The newest
 *  can be today's and coming workouts, and skipped or unperformed ones, which are passed over, so
 *  the read reaches well past them */
export const WORKOUTS_READ_FOR_NOTE = 20;

/** an ISO time as milliseconds, NaN when it is missing or unreadable */
function timeOf(iso: string | undefined): number {
  if (typeof iso !== 'string' || iso === '') {
    return NaN;
  }
  const time = DateTime.fromISO(iso);
  return time.isValid ? time.toMillis() : NaN;
}

/**
 * R9: the newest change for this sub-slot, when the program entry no longer carries its stamp.
 *
 * Equal stamps are the normal case. A change for a different exercise means the slot was rebuilt
 * or switched since (sub-slot ids are reused), so it says nothing about an overwrite. Otherwise a
 * change newer than the entry's stamp, or an entry with no stamp at all, means something wrote the
 * entry without the person's change: a session on a phone that had not received it yet. That is
 * only proven when a set was concluded on the entry AFTER the change: the change record and the
 * plan sync separately, and a record that simply arrived before the plan must not be reported.
 */
export function overwrittenChange(docsNewestFirst: ExerciseChangeDoc[], live: Plan_SubSlot | undefined): ExerciseChangeDoc | undefined {
  const newest = docsNewestFirst[0];
  if (!newest || !live || newest.exercise_id !== live.exercise_id) {
    return undefined;
  }
  const liveStamp = coachChangeStampOf(live);
  if (liveStamp === newest.changed_utc) {
    return undefined;
  }
  const changed = timeOf(newest.changed_utc);
  if (Number.isNaN(changed)) {
    return undefined;
  }
  const concludedAfterChange = timeOf(live.lastTimeConcluded_utc) > changed;
  const stampOlder = liveStamp === undefined || changed > timeOf(liveStamp);
  return stampOlder && concludedAfterChange ? newest : undefined;
}

/**
 * R8: the coach changes the client's note is about, oldest first; empty for no note.
 *
 * Every change a COACH made to this exercise after the previous workout that held the sub-slot:
 * this workout is the first one after them. A coach who saved twice (a switch to DUP, then a set
 * count) gets both into the note (owner decision, 2026-10-16). A person's own change (role 'self')
 * gets no note. No previous workout means every coach change is new to the client.
 */
export function coachNoteChanges(
  docsNewestFirst: ExerciseChangeDoc[],
  exerciseId: string,
  previousWorkoutTime_utc: string | undefined,
): ExerciseChangeDoc[] {
  const previous = timeOf(previousWorkoutTime_utc);
  return docsNewestFirst
    .filter(doc => doc.author?.role === 'coach' && doc.exercise_id === exerciseId)
    .filter(doc => {
      const changed = timeOf(doc.changed_utc);
      return !Number.isNaN(changed) && (Number.isNaN(previous) || changed > previous);
    })
    .reverse();
}

/**
 * R8: the workout before `workoutTime_utc` in which the client really trained this sub-slot (at
 * least one finished set), from the exercise's recent workouts (any order). Undefined when there
 * is none. A coming or skipped workout is not "the previous workout": counting one would either
 * show old changes again or hide a new one.
 */
export function previousWorkoutWithSubSlot(workouts: Log_Workout[], subSlotId: string, workoutTime_utc: string): Log_Workout | undefined {
  const before = timeOf(workoutTime_utc);
  let previous: Log_Workout | undefined;
  for (const workout of workouts) {
    const time = timeOf(workout?.time_utc);
    if (Number.isNaN(time) || !(time < before)) {
      continue;
    }
    if (!workout.exercises?.some(exercise => exercise?.subSlotId === subSlotId
      && (exercise.sets ?? []).some(set => set?.state === LogRecordState.Done))) {
      continue;
    }
    if (!previous || time > timeOf(previous.time_utc)) {
      previous = workout;
    }
  }
  return previous;
}

/**
 * R6: is a volume question raised on this workout still open?
 *
 * Normally the session's own paths decide, as before #6428 (any of them still naming a workout).
 * When the program entry carries a different coach stamp than the session's snapshot, the session's
 * copies are out of date — a coach who changed the set count cleared the question in the program
 * only — so the program's own paths decide, and only a pointer at THIS workout keeps it open.
 */
export function volumeQuestionOpen(
  guardOn: boolean,
  live: Plan_SubSlot | undefined,
  sessionPaths: (Plan_ExercisePath | undefined)[],
  workoutId: string,
): boolean {
  if (guardOn) {
    return (live?.exercisePaths ?? []).some(path => path?.autoregulationWorkoutLog_id === workoutId);
  }
  return sessionPaths.some(path => !!path?.autoregulationWorkoutLog_id);
}

/**
 * R6: can the page prove a coach answered this kind of question after the workout that raised it?
 * Only then is a card with nothing left to answer an expected outcome rather than a problem.
 */
export function coachAnsweredQuestion(
  docs: ExerciseChangeDoc[],
  workoutTime_utc: string,
  field: ExerciseChangeField.PlateauAnswered | ExerciseChangeField.VolumeQuestionAnswered,
): boolean {
  const workoutTime = timeOf(workoutTime_utc);
  if (Number.isNaN(workoutTime)) {
    return false;
  }
  return docs.some(doc => doc.author?.role === 'coach'
    && timeOf(doc.changed_utc) > workoutTime
    && (doc.changes ?? []).some(change => change?.field === field));
}
